/**
 * Kit Pod brand mark — side-view whale silhouette + wordmark.
 *
 * Glyph echoes the sticker mascot used in the hero (chunky teardrop
 * body, fluke tail, water spout, eye) but recolored to brand accent
 * (burnt honey). The mascot stays purple for visual contrast against
 * the honey UI; the glyph is the monochrome counterpart used in nav,
 * footer and favicon where 18–32px legibility matters.
 */

import clsx from 'clsx';

interface Props {
  size?: 'sm' | 'md' | 'lg';
  showWordmark?: boolean;
  className?: string;
}

/** Source sticker dims — 512×341. Preserve aspect or it squashes. */
const MASCOT_ASPECT = 341 / 512;

export function BrandMark({
  size = 'md',
  showWordmark = true,
  className,
}: Props) {
  // The sticker has ~25% internal padding (white outline + breathing
  // room), so the visible whale is much smaller than the box. We
  // pick a generous WIDTH and derive the height from the source
  // aspect; numbers are noticeably bigger than the old SVG glyph
  // because the silhouette only fills ~70% of those pixels.
  const wPx = size === 'sm' ? 44 : size === 'md' ? 56 : 80;
  const hPx = Math.round(wPx * MASCOT_ASPECT);
  const textSize =
    size === 'sm'
      ? 'text-[15px]'
      : size === 'md'
        ? 'text-[19px]'
        : 'text-[28px]';
  return (
    <div className={clsx('inline-flex items-center gap-2', className)}>
      <img
        src="/whale-mascot.webp"
        alt=""
        aria-hidden
        width={wPx}
        height={hPx}
        className="shrink-0"
        style={{ width: wPx, height: hPx }}
      />
      {showWordmark ? (
        <span
          className={`pb-display leading-none tracking-tight ${textSize}`}
          aria-label="Kit Pod"
        >
          Kit <span className="text-pb-accent">Pod</span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Side-view whale: teardrop body facing left, forked fluke tail on
 * the right, pectoral fin underneath, eye + water spout droplets
 * above the head. Reads unambiguously at 20px.
 */
export function WhaleGlyph({
  size = 26,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={(size * 22) / 32}
      viewBox="0 0 32 22"
      fill="none"
      aria-hidden
      className={className}
    >
      {/* Water spout — three droplets above the head */}
      <path
        d="M8 4.6 Q7 2.4 8 1 Q9 2.4 8 4.6 Z
           M5.8 4.4 Q4.8 3 5.5 1.8 Q6.3 3 5.8 4.4 Z
           M10.2 4.4 Q11.2 3 10.5 1.8 Q9.7 3 10.2 4.4 Z"
        fill="var(--pb-accent)"
        opacity="0.6"
      />
      {/* Body — chunky teardrop tapering toward the tail */}
      <path
        d="M3 12
           C 3 8, 7 5.6, 12 5.6
           C 18 5.6, 22.6 8, 24 11.4
           L 28.5 9
           C 29 8.8, 29.5 9.2, 29.3 9.7
           L 28 12.2
           L 29.6 14.8
           C 29.85 15.3, 29.4 15.75, 28.9 15.55
           L 24 13.2
           C 22.6 16.6, 18 19, 12 19
           C 7 19, 3 16.5, 3 12 Z"
        fill="var(--pb-accent)"
      />
      {/* Pectoral fin underneath */}
      <path
        d="M9.5 17.6 Q11.2 20.8 14 19.9 Q12.3 18.8 11.2 17.4 Z"
        fill="var(--pb-accent)"
        opacity="0.8"
      />
      {/* Eye */}
      <circle cx="6.5" cy="10.6" r="0.95" fill="#17212b" />
      {/* Mouth — subtle smile line. Dark color is hardcoded (not
          tg-theme-bg-color) because on TG light theme the variable
          resolves to white and the mouth disappears against the
          honey body. Dark ink on honey reads on both themes. */}
      <path
        d="M3.4 13 Q5 13.9 7.4 13.7"
        stroke="#17212b"
        strokeWidth="0.55"
        strokeLinecap="round"
        fill="none"
        opacity="0.55"
      />
    </svg>
  );
}

/**
 * "Pod" — three whales in loose formation over a dotted waterline.
 * Used as the Welcome hero illustration and on the HowItWorks page.
 */
export function WhalePod({
  size = 200,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={(size * 160) / 240}
      viewBox="0 0 240 160"
      fill="none"
      aria-hidden
      className={className}
    >
      {/* Dotted waterline */}
      <path
        d="M10 90 Q60 84 120 90 T230 90"
        stroke="var(--pb-border)"
        strokeDasharray="2 5"
        strokeWidth="1"
        fill="none"
      />
      {/* Big whale — center */}
      <g transform="translate(60 30)">
        <WhaleGlyph size={120} />
      </g>
      {/* Small whale top-left, slightly faded */}
      <g transform="translate(8 24) rotate(-6)" opacity="0.78">
        <WhaleGlyph size={62} />
      </g>
      {/* Small whale bottom-right */}
      <g transform="translate(168 96) rotate(4)" opacity="0.78">
        <WhaleGlyph size={58} />
      </g>
      {/* Signal dots */}
      <g fill="var(--pb-accent)" opacity="0.55">
        <circle cx="38" cy="14" r="1.8" />
        <circle cx="208" cy="40" r="1.8" />
        <circle cx="26" cy="138" r="1.8" />
        <circle cx="218" cy="92" r="1.8" />
      </g>
    </svg>
  );
}
